import { lazy, Suspense, useState, useCallback, ReactNode } from 'react';
import {
  ExpandedViewContext,
  ExpandedViewContent,
  ExpandedViewContextType,
} from 'src/shared/components/thread-store/context/ExpandedViewContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'src/shared/components/ui/sheets/sheet';
import { Loading } from 'src/shared/components/ui/loaders/loading';

const LazyCsvEditorModal = lazy(() => import('../csv/CsvEditorModal'));

interface WebExpandedViewProviderProps {
  children: ReactNode;
}

/**
 * Web implementation of ExpandedViewProvider.
 * Uses a Sheet/Modal component to display expanded content.
 * This is used in the main frontend (messages module sidepanel).
 */
export function WebExpandedViewProvider({ children }: WebExpandedViewProviderProps) {
  const [content, setContent] = useState<ExpandedViewContent | null>(null);

  const openExpandedView = useCallback((newContent: ExpandedViewContent) => {
    setContent(newContent);
  }, []);

  const closeExpandedView = useCallback(() => {
    setContent(null);
  }, []);

  const contextValue: ExpandedViewContextType = {
    openExpandedView,
    closeExpandedView,
    isOpen: content !== null,
    content,
  };

  return (
    <ExpandedViewContext.Provider value={contextValue}>
      {children}
      <Sheet open={content !== null} onOpenChange={(open) => !open && setContent(null)}>
        <SheetContent
          side="right"
          className="w-[90vw] max-w-[900px] sm:max-w-[900px] p-0 overflow-hidden [&>button]:hidden"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{content?.data.title || 'CSV Editor'}</SheetTitle>
          </SheetHeader>
          {content && <CsvEditorModalContent content={content} onClose={closeExpandedView} />}
        </SheetContent>
      </Sheet>
    </ExpandedViewContext.Provider>
  );
}

interface CsvEditorModalContentProps {
  content: ExpandedViewContent;
  onClose: () => void;
}

function CsvEditorModalContent({ content, onClose }: CsvEditorModalContentProps) {
  return (
    <Suspense
      fallback={
        <div className="flex-center h-full">
          <Loading />
        </div>
      }
    >
      <LazyCsvEditorModal
        threadStoreId={content.data.threadStoreId}
        integrationId={content.data.integrationId}
        threadId={content.data.threadId}
        valueToApproveId={content.data.valueToApproveId}
        editable={content.data.editable}
        title={content.data.title}
        file={content.data.file}
        containsHeader={content.data.containsHeader}
        filename={content.data.filename}
        onClose={onClose}
      />
    </Suspense>
  );
}
